import { memo, useEffect, useRef, useState } from 'react';
import {
  CenterFocusStrong,
  CheckCircleOutline,
  ExpandMore,
  LinkOff,
  NoteAdd,
  ThumbDown,
  ThumbDownOutlined,
  ThumbUp,
  ThumbUpOutlined,
  Visibility,
} from '@mui/icons-material';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import {
  Box,
  ButtonBase,
  Checkbox,
  Chip,
  Collapse,
  IconButton,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { SECURITY_NAME_ALIAS } from 'common/constants/cap-table/capTable';
import AIFindingBody from 'components/AIBasePopover/AIFindingBody';
import CitationBody from 'components/AIBasePopover/CitationBody';
import { scrollIntoViewUntilSettled } from 'components/FileViewer/pdf-viewer/scrollIntoViewUntilSettled';
import { hasLiveAiCitation, isReviewItemActionable } from 'pages/CapTable/cap-table/utilities/reviewAggregator';
import type { ReviewEvidenceKind } from 'store/useReviewStore';
import { GREEN, RED } from 'theme/prismDesignTokens';
import { ReviewCardProps } from './ReviewCard.props';
import {
  cardSx,
  chipSx,
  citedValueSx,
  detailWrapperSx,
  documentNameSx,
  evidenceOnlyChipSx,
  noteFieldSx,
  noteToggleSx,
  reopenButtonSx,
  statusLabelSx,
  thumbButtonSx,
  titleSx,
  valueSx,
} from './ReviewCard.styles';
import { hasReviewExcelJump } from './reviewDocJump';

// Evidence chip wording. Counts are guaranteed > 0 by the aggregator, so the
// number is always shown for this slice.
const EVIDENCE_LABEL: Record<ReviewEvidenceKind, (count: number) => string> = {
  citations: count => `${count} ${count === 1 ? 'Citation' : 'Citations'}`,
  aiExtractions: count => `${count} AI Data Extraction${count === 1 ? '' : 's'}`,
  excel: count => `${count} Excel Referenced Value${count === 1 ? '' : 's'}`,
};

const stop = (e: React.SyntheticEvent) => e.stopPropagation();

const ReviewCard = ({
  item,
  active,
  selected = false,
  reviewed = false,
  onAccept,
  onDecline,
  onAcceptValueRejectCitation,
  onActivate,
  onToggleSelect,
  onReopen,
  onHoverChange,
  onRevealPdf,
  onRevealExcel,
  documentName,
}: ReviewCardProps) => {
  // Every card starts collapsed: the long AI commentary/evidence is hidden behind
  // the show-more expander (key info stays visible) so a stack of pending
  // suggestions is scannable. A small optional note field on pending cards is also
  // opt-in so it stays subordinate.
  const [detailOpen, setDetailOpen] = useState(false);
  const [noteOpen, setNoteOpen] = useState(false);
  const [reason, setReason] = useState('');

  // SCAL-9182: activation can now originate from the GRID (clicking a cited cell),
  // so an active card smooth-scrolls itself into the pane's view. `nearest` no-ops
  // when it is already fully visible - activating by clicking the card itself never
  // scrolls. Guarded by the shared settle loop, NOT a one-shot scroll: the pane can
  // mount inside the drawer's expand transition (cell click with the drawer
  // minimized), where a one-shot measures the pre-transition layout and parks a
  // deep card off-view - the guard re-lands it once the layout settles. The card is
  // re-resolved by id every frame, so a re-render never strands the loop.
  const cardRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (!active || !cardRef.current) {
      return undefined;
    }
    return scrollIntoViewUntilSettled(cardRef.current.ownerDocument, `review-card-${item.id}`, {
      block: 'nearest',
      containerSelector: '[data-testid="review-panel-list"]',
    });
  }, [active, item.id]);

  const handleActivate = () => onActivate?.(item);
  const handleKey = (e: React.KeyboardEvent) => {
    // Only keydowns on the card itself may activate it: bubbled Enter/Space from a
    // nested control (crosshair, eyeball, thumbs, note toggle) must keep that
    // control's own button semantics, which preventDefault here would cancel.
    if (e.target !== e.currentTarget) {
      return;
    }
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleActivate();
    }
  };

  // Persisted evidence names its exact source; live references use the panel's lookup.
  const docName = item.citationDocumentName ?? documentName ?? item.documentNames[0];
  const hasDetail = item.confidence != null || item.commentary.length > 0 || item.citationTexts.length > 0;
  const accepted = item.status === 'accepted';

  // Two-row card: the label (security name + metric) shares the top row with the
  // accept/decline thumbs, and the suggested value drops to its own full-width line
  // below. `label` is the composed title minus its trailing value segment; the value
  // row mirrors the aggregator's dedup (hidden when it just repeats the security name,
  // e.g. the Security-Name field) so we never show "Series D" twice.
  const label = `${item.securityLabel} - ${item.fieldLabel}`;
  const showValue = !!item.displayedValue && item.displayedValue !== item.securityLabel;
  // Excel evidence that is settleable in its own right. Read optionally so this card
  // labels correctly both before and after the mapping-accept flag lands.
  const canSettleMapping = (item as { canSettleMapping?: boolean }).canSettleMapping === true;
  // Evidence-only card: the finding is a citation (PDF) or an Excel reference with NO
  // value to accept, so it is marked as such and never left looking like a suggestion.
  const hasCitationEvidence = !!(item.primaryCitation || item.canSettleCitation);
  const evidenceOnly = !item.canSettle && (hasCitationEvidence || canSettleMapping || !!item.primaryMappingReference);
  // Name the evidence the reviewer can actually act on: a pending mapping owns the
  // card even when an already-settled citation is attached too.
  const citationOnly = hasCitationEvidence && !canSettleMapping;
  // Headline-less cards surface the cited evidence text instead, on a distinct
  // line. Only the Security-Name field dedups against the security label - any
  // other field (e.g. Legal Name) may legitimately equal it.
  const showCitedValue =
    !item.displayedValue &&
    !!item.citedValue &&
    (item.fieldAlias !== SECURITY_NAME_ALIAS || item.citedValue !== item.securityLabel);

  // Eyeball: reveal the card's PRIMARY citation source in the PDF viewer. Rendered
  // only when the item has a document-backed citation. stop() keeps the body's
  // click-to-activate from also firing, so the eyeball is the SINGLE PDF trigger
  // and never changes selection/scroll.
  const sourceButton = item.primaryCitation && (
    <Tooltip title="View source in PDF">
      <IconButton
        size="small"
        aria-label="view source in PDF"
        sx={noteToggleSx}
        onClick={e => {
          stop(e);
          onRevealPdf?.(item);
        }}>
        <Visibility fontSize="small" />
      </IconButton>
    </Tooltip>
  );

  // Crosshair (SCAL-9289): reveal the card's Excel mapping source - the same jump
  // body activation fires - without changing the active selection. Rendered only
  // when the jump would actually SNAP to the mapped cell (hasReviewExcelJump), so
  // citation-only cards never show an affordance that would silently no-op.
  const excelSourceButton = hasReviewExcelJump(item) && (
    <Tooltip title="Show source cell in document">
      <IconButton
        size="small"
        aria-label="show source cell in document"
        data-testid="review-reveal-source-cell"
        sx={noteToggleSx}
        onClick={e => {
          stop(e);
          onRevealExcel?.(item);
        }}>
        <CenterFocusStrong fontSize="small" />
      </IconButton>
    </Tooltip>
  );

  // Reviewed-card status label, shared by the plain and the clickable (reopen)
  // renderings so the two can never drift.
  const statusLabel = (
    <Typography component="span" sx={statusLabelSx(accepted)}>
      {accepted ? <CheckCircleOutline fontSize="small" /> : <HighlightOffIcon fontSize="small" />}
      {accepted ? 'Accepted' : 'Dismissed'}
    </Typography>
  );

  // No stopPropagation here: a single click anywhere on the card body (including
  // the commentary/evidence) should activate the card, not just the title row.
  const detailBody = hasDetail && (
    <Box sx={detailWrapperSx}>
      <AIFindingBody confidence={item.confidence} commentary={item.commentary} />
      {item.citationTexts.length > 0 && (
        <CitationBody
          sectionLabel="Evidence"
          plainTexts={item.citationTexts}
          documentName={item.citationDocumentName}
        />
      )}
    </Box>
  );

  return (
    <Box
      ref={cardRef}
      role="button"
      tabIndex={0}
      id={`review-card-${item.id}`}
      data-testid={`review-card-${item.id}`}
      sx={cardSx(active, reviewed)}
      onClick={handleActivate}
      onKeyDown={handleKey}
      onMouseEnter={() => onHoverChange?.(item, true)}
      onMouseLeave={() => onHoverChange?.(item, false)}
      onFocus={() => onHoverChange?.(item, true)}
      onBlur={() => onHoverChange?.(item, false)}>
      <Stack direction="row" alignItems="flex-start" justifyContent="space-between" gap={0.5}>
        <Stack direction="row" alignItems="flex-start" gap={0.5} sx={{ minWidth: 0 }}>
          {/* Pending + actionable: checkbox drives multi-select and stops the card jump. */}
          {!reviewed && isReviewItemActionable(item) && (
            <Checkbox
              size="small"
              checked={selected}
              slotProps={{ input: { 'aria-label': 'select review item' } }}
              sx={{ p: 0.25, mt: '-2px' }}
              onClick={stop}
              onChange={() => onToggleSelect?.(item)}
            />
          )}
          <Typography sx={titleSx}>{label}</Typography>
        </Stack>

        {reviewed ? (
          <Stack direction="row" alignItems="center" gap={0.25} sx={{ flexShrink: 0 }}>
            {sourceButton}
            {excelSourceButton}
            {/* SCAL-9262: on settleable items the status label doubles as the
                UNDO affordance - clicking it reopens the decision, moving the
                card back to pending where accept/decline re-present. stop() so
                changing the decision never also jumps the grid selection. */}
            {onReopen && isReviewItemActionable(item) ? (
              <Tooltip title="Change decision">
                <ButtonBase
                  aria-label="reopen review item"
                  sx={reopenButtonSx(accepted)}
                  onClick={e => {
                    stop(e);
                    onReopen(item);
                  }}>
                  {statusLabel}
                </ButtonBase>
              </Tooltip>
            ) : (
              statusLabel
            )}
          </Stack>
        ) : (
          <Stack direction="row" alignItems="center" gap={0.25} sx={{ flexShrink: 0 }}>
            {sourceButton}
            {excelSourceButton}
            {/* Thumbs settle whichever surface the item carries: FIELD metadata,
                its ACTIVE citation (SCAL-8963), or its AI Excel mapping rows
                (SCAL-9667). An item with none - a manual entry - shows only its
                reveal icon(s). */}
            {isReviewItemActionable(item) && (
              <>
                <Tooltip title="Accept suggestion">
                  <IconButton
                    size="small"
                    aria-label="accept review item"
                    sx={thumbButtonSx(GREEN[500])}
                    onClick={e => {
                      stop(e);
                      onAccept?.(item, reason.trim() || undefined);
                    }}>
                    <ThumbUpOutlined className="thumb-outline" fontSize="small" />
                    <ThumbUp className="thumb-fill" fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Decline suggestion">
                  <IconButton
                    size="small"
                    aria-label="decline review item"
                    sx={thumbButtonSx(RED[500])}
                    onClick={e => {
                      stop(e);
                      onDecline?.(item, reason.trim() || undefined);
                    }}>
                    <ThumbDownOutlined className="thumb-outline" fontSize="small" />
                    <ThumbDown className="thumb-fill" fontSize="small" />
                  </IconButton>
                </Tooltip>
                {item.canSettle && item.primaryCitation && onAcceptValueRejectCitation ? (
                  <Tooltip title="Accept value, reject citation">
                    <IconButton
                      size="small"
                      aria-label="accept value reject citation"
                      sx={thumbButtonSx(GREEN[500])}
                      onClick={e => {
                        stop(e);
                        onAcceptValueRejectCitation(item);
                      }}>
                      <LinkOff fontSize="small" />
                    </IconButton>
                  </Tooltip>
                ) : null}
              </>
            )}
            {/* Unobtrusive "add note" toggle - default accept/decline stays one-click.
                FIELD settles only: the citation review endpoint takes no reason. */}
            {item.canSettle && (
              <Tooltip title={noteOpen ? 'Hide note' : 'Add a note'}>
                <IconButton
                  size="small"
                  aria-label="add review note"
                  sx={noteToggleSx}
                  onClick={e => {
                    stop(e);
                    setNoteOpen(o => !o);
                  }}>
                  <NoteAdd fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </Stack>
        )}
      </Stack>

      {/* The suggested value gets its own full-width line beneath the label/thumbs
          row, so a long value can never crowd or clip the accept/decline controls.
          Headline-less cards show the cited evidence text there instead. */}
      {showValue && <Typography sx={valueSx}>{item.displayedValue}</Typography>}
      {showCitedValue && (
        <Typography sx={citedValueSx} data-testid="review-cited-value">
          {item.citedValue}
        </Typography>
      )}

      {!reviewed && (
        <Collapse in={noteOpen} unmountOnExit>
          <TextField
            fullWidth
            multiline
            size="small"
            variant="standard"
            placeholder="Optional note for this decision"
            value={reason}
            sx={noteFieldSx}
            onClick={stop}
            onChange={e => setReason(e.target.value)}
          />
        </Collapse>
      )}

      <Stack direction="row" flexWrap="wrap" gap={0.5} sx={{ mt: 1, minWidth: 0 }}>
        {/* Provenance chip (SCAL-9159): AI-created vs analyst/manual entry, derived
            by the aggregator (created_source authoritative, job fallback). */}
        <Chip size="small" sx={chipSx} data-testid="review-provenance-chip" label={item.aiCreated ? 'AI' : 'Manual'} />
        {/* Value-change chip: whether accepting SUPERSEDES an existing value
            ("Merge", the prev -> ai case) or fills an empty cell ("New").
            Citation-only items carry no field value change, so no chip. */}
        {item.valueChange && (
          <Chip
            size="small"
            sx={chipSx}
            data-testid="review-value-change-chip"
            label={item.valueChange === 'merge' ? 'Merge' : 'New'}
          />
        )}
        {/* Evidence-only marker: nothing to accept, so it takes the value-change
            slot and wears the grid badge's colour for this cell - its live review
            status plus the citations' own provenance (the card-level aiCreated
            also counts AI Excel mappings, which the grid badge ignores). */}
        {evidenceOnly && (
          <Chip
            size="small"
            sx={evidenceOnlyChipSx(citationOnly, item.status === 'pending', hasLiveAiCitation(item.citations))}
            data-testid="review-evidence-only-chip"
            label={citationOnly ? 'Citation only' : 'Reference only'}
          />
        )}
        {/* Echo-suggestion dedupe (backend auto-settles duplicates as accepted
            with review_reason='duplicate'): informational, muted. */}
        {item.duplicate && <Chip size="small" sx={chipSx} data-testid="review-duplicate-chip" label="Duplicate" />}
        {/* SCAL-9262: un-accept left the cell's manually-edited value in place
            (it differed from what the accept wrote); the reopened card flags it. */}
        {item.valueKept && <Chip size="small" sx={chipSx} data-testid="review-value-kept-chip" label="Value kept" />}
        {item.evidence.map(group => (
          <Chip key={group.kind} size="small" sx={chipSx} label={EVIDENCE_LABEL[group.kind](group.count ?? 0)} />
        ))}
      </Stack>

      {/* All cards (pending and reviewed) collapse the long detail behind one
          show-more expander, so the list stays scannable; key info above stays
          visible while collapsed. */}
      {hasDetail && (
        <>
          <IconButton
            size="small"
            aria-label="toggle review detail"
            aria-expanded={detailOpen}
            sx={{ mt: 0.5, p: 0.25, color: 'text.secondary' }}
            onClick={e => {
              stop(e);
              setDetailOpen(o => !o);
            }}>
            <ExpandMore
              fontSize="small"
              sx={{ transform: detailOpen ? 'rotate(180deg)' : 'none', transition: 'transform 150ms' }}
            />
          </IconButton>
          <Collapse in={detailOpen} unmountOnExit>
            {detailBody}
          </Collapse>
        </>
      )}

      {docName && (
        <Typography sx={documentNameSx}>
          <DescriptionOutlinedIcon sx={{ fontSize: 14, flexShrink: 0 }} />
          {docName}
        </Typography>
      )}
    </Box>
  );
};

// Memoized: selecting one card's checkbox flips only that card's `selected` prop,
// so unrelated cards (with stable item/handler props from the panel) skip the
// re-render. Without this the first selection re-rendered every card at once,
// which is what made the first checkbox feel laggy.
export default memo(ReviewCard);
